// Embed branches of the service layer (leaf module). Each is a no-op outside embed and hosted mode.
import type { AxiosRequestConfig } from 'axios'
import { type DpopKeyStore, getOrCreateDpopKeyPair } from '../utils/embed-dpop-key'
import { buildDpopProof, DPOP_HEADER, dpopPath } from '../utils/embed-dpop-proof'
import {
  dispatchEmbedSessionExpired,
  getEmbedSession,
  isEmbedMode,
  isHostedMode,
  type REmbedSession,
  toEmbedPath,
} from '../utils/embed-session'

// The DPoP proof header for one request, or {} when no key is available (never throws). hodor
// ignores, binds or requires proofs per its mode. `store` is a test seam.
export const embedDpopHeaders = async (
  method: string,
  url: string,
  session: REmbedSession | null,
  store?: DpopKeyStore,
): Promise<Record<string, string>> => {
  const bearer = session?.session_token
  // Hosted links are minted before the page exists, so their sessions are never key-bound.
  if (!bearer || isHostedMode()) {
    return {}
  }
  try {
    const keyPair = await getOrCreateDpopKeyPair(store)
    if (!keyPair) {
      return {}
    }
    return {
      [DPOP_HEADER]: await buildDpopProof(keyPair, {
        method,
        path: dpopPath(url),
        bearer,
      }),
    }
  } catch {
    return {}
  }
}

// Embed identity. Client-Type embed_app is the wall between embed and console tokens; no Aom_uuid
// or Browser-Session-Id. With no session the empty token 401s into the expiry overlay.
const embedIdentityHeaders = (session: REmbedSession | null): Record<string, string> => ({
  'Client-Type': 'embed_app',
  Authorization: `Token ${session?.session_token ?? ''}`,
  Region: session?.region ?? '',
})

// Header precedence: defaults, then call-site overrides, then the identity headers, which no call
// site can override.
export const createEmbedHeaders = (
  session: REmbedSession | null = getEmbedSession(),
  overrides?: Record<string, string>,
): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...overrides,
  ...embedIdentityHeaders(session),
})

// The URL axios will actually request (mirrors axios buildFullPath). A doubled slash in the proof's
// htu 401s, and an embed 401 tears the iframe down and re-mints.
export const embedProofUrl = (baseURL: string, url: string): string => {
  if (!baseURL || /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) {
    return url || baseURL
  }
  return url ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : baseURL
}

// Rewrites onto hodor's /v{n}/embed gateway and resolves {{region}} from the embed session.
export const applyEmbedRequestConfig = async (
  config: AxiosRequestConfig,
): Promise<AxiosRequestConfig> => {
  if (!isEmbedMode()) {
    return config
  }
  const session = getEmbedSession()
  // Fail closed with no session: surface expiry instead of calling a malformed regional host.
  if (!session?.region) {
    dispatchEmbedSessionExpired()
    throw new Error('Embed session expired')
  }
  let baseURL = config.baseURL ?? ''
  let url = config.url ?? ''

  if (baseURL.includes('{{region}}')) {
    baseURL = baseURL.replace('{{region}}', session?.region ?? '')
  }
  // Rewrite whichever part carries the /v{n}/ segment.
  if (/\/v\d+\//.test(url)) {
    url = toEmbedPath(url)
  } else {
    baseURL = toEmbedPath(baseURL)
  }
  // Signed over the rewritten, axios-joined URL: htu must be the path hodor receives.
  const proof = await embedDpopHeaders(
    (config.method ?? 'get').toUpperCase(),
    embedProofUrl(baseURL, url),
    session,
  )
  return {
    ...config,
    baseURL,
    url,
    // The per-request proof rides outside the identity wall.
    headers: {
      ...createEmbedHeaders(session, config.headers as Record<string, string> | undefined),
      ...proof,
    },
  }
}

// An embed 401 means the embed session died: fire the expiry event, never window.cx.logout
// (that would wipe a console session in another tab).
export const handleEmbedUnauthorized = (status?: number): boolean => {
  if (!isEmbedMode() || status !== 401) {
    return false
  }
  dispatchEmbedSessionExpired()
  return true
}
